import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import Divider from '@mui/material/Divider';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import TableChartIcon from '@mui/icons-material/TableChart';

import { PageContainer } from '../layout/PageContainer';
import {
  TOP_N,
  averageBreakdownRate,
  averagePriceEur,
  bodyTypeLabel,
  topModelsByBodyType,
  topModelsByPriceBand,
  type RankedCategory,
  type ReliabilityModel,
} from '../../domain/reliability';
import { useReliability } from '../../hooks/useReliability';
import { ModelName, ReliabilitySources, ReliabilityUnavailable, ScoreChip } from './ReliabilityShared';

type Dimension = 'price' | 'body';

const euro = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });

function bestTuevResult(model: ReliabilityModel): string | null {
  const entries = Object.entries(model.tuev?.byAgeClass ?? {});
  if (!entries.length) {
    return null;
  }
  const [ageClass, observation] = entries.sort((a, b) => a[1].defectRate - b[1].defectRate)[0];
  return `TÜV ${observation.defectRate.toFixed(1)}% defects at ${ageClass} yrs`;
}

function RankedRow({ model, rank }: { model: ReliabilityModel; rank: number }) {
  const price = averagePriceEur(model);
  const adacRate = averageBreakdownRate(model);
  const tuev = bestTuevResult(model);
  const bodyTypes = [...new Set(model.bodyTypes.map(bodyTypeLabel).filter(Boolean))];

  return (
    <Stack direction="row" spacing={2} alignItems="center" sx={{ py: 1.5 }}>
      <Typography variant="h6" color="text.secondary" sx={{ width: 32, textAlign: 'right', flexShrink: 0 }}>
        {rank}
      </Typography>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <ModelName model={model} variant="subtitle1" />
        <Typography variant="body2" color="text.secondary">
          {[
            adacRate !== null ? `ADAC ${adacRate.toFixed(1)} breakdowns / 1,000 cars` : null,
            tuev,
            bodyTypes.join(', ') || null,
          ]
            .filter(Boolean)
            .join(' · ')}
        </Typography>
      </Box>
      <Stack alignItems="flex-end" spacing={0.5} sx={{ flexShrink: 0 }}>
        <ScoreChip score={model.score} />
        {price !== null && (
          <Typography variant="body2" color="text.secondary">
            avg. {euro.format(price)}
          </Typography>
        )}
      </Stack>
    </Stack>
  );
}

function CategoryRanking<C extends { id: string; label: string }>({ categories }: { categories: RankedCategory<C>[] }) {
  const [activeId, setActiveId] = useState(() => (categories.find((c) => c.total > 0) ?? categories[0])?.category.id);
  const active = categories.find((c) => c.category.id === activeId) ?? categories[0];

  return (
    <Card>
      <Tabs
        value={active.category.id}
        onChange={(_, value) => setActiveId(value)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ borderBottom: 1, borderColor: 'divider', px: 1 }}
      >
        {categories.map(({ category, total }) => (
          <Tab key={category.id} value={category.id} label={`${category.label} (${total})`} disabled={total === 0} />
        ))}
      </Tabs>
      <Box sx={{ px: { xs: 2, sm: 3 }, py: 1 }}>
        {active.models.length === 0 ? (
          <Typography color="text.secondary" sx={{ py: 3 }}>
            No rated models in this category yet.
          </Typography>
        ) : (
          active.models.map((model, index) => (
            <Box key={model.id}>
              {index > 0 && <Divider />}
              <RankedRow model={model} rank={index + 1} />
            </Box>
          ))
        )}
        {active.total > TOP_N && (
          <Typography variant="body2" color="text.secondary" sx={{ py: 1.5 }}>
            Showing the top {TOP_N} of {active.total} rated models.
          </Typography>
        )}
      </Box>
    </Card>
  );
}

export function BestCarsPage() {
  const { data, isLoading, isError } = useReliability();
  const [dimension, setDimension] = useState<Dimension>('price');

  const byPrice = useMemo(() => (data ? topModelsByPriceBand(data.models) : []), [data]);
  const byBody = useMemo(() => (data ? topModelsByBodyType(data.models) : []), [data]);

  return (
    <PageContainer>
      <Typography variant="h3" component="h1" gutterBottom>
        Most reliable cars
      </Typography>
      <Typography color="text.secondary" sx={{ maxWidth: 760, mb: 3 }}>
        The top {TOP_N} models in each price range and body type, ranked by a reliability score built from the ADAC
        breakdown statistics and the TÜV roadworthiness-inspection report. A score of 50 means as reliable as the average
        car of the same age. Prices are current average listings on mobile.bg.
      </Typography>

      {isLoading && <Skeleton variant="rounded" height={480} />}
      {(isError || (!isLoading && !data)) && <ReliabilityUnavailable />}

      {data && (
        <Stack spacing={3}>
          <ReliabilitySources document={data} />

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ sm: 'center' }}>
            <ToggleButtonGroup
              exclusive
              color="primary"
              value={dimension}
              onChange={(_, value: Dimension | null) => value && setDimension(value)}
            >
              <ToggleButton value="price">By price range</ToggleButton>
              <ToggleButton value="body">By body type</ToggleButton>
            </ToggleButtonGroup>
            <Button component={Link} to="/reliability" variant="outlined" startIcon={<TableChartIcon />}>
              Explore all reliability data
            </Button>
          </Stack>

          {dimension === 'price' ? (
            <CategoryRanking key="price" categories={byPrice} />
          ) : (
            <CategoryRanking key="body" categories={byBody} />
          )}

          <Typography variant="body2" color="text.secondary">
            Score scale: Excellent 70+ · Good 58–69 · Average 45–57 · Below average 33–44 · Poor under 33. Models
            with only one or two data points are pulled towards 50 until more data confirms them.
          </Typography>
        </Stack>
      )}
    </PageContainer>
  );
}
